import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase';
import { collection, onSnapshot, query, addDoc, setDoc, updateDoc, deleteDoc, doc, orderBy, serverTimestamp } from 'firebase/firestore';
import { FaEdit, FaTrash, FaPlus, FaLayerGroup, FaTags, FaListUl, FaGlobe, FaSearch, FaFilter } from 'react-icons/fa';
import { renderIcon } from '../../utils/iconRenderer';
import ConfirmModal from '../ConfirmModal';

const LANGUAGES = [
    { code: 'en', alt: 'in', label: 'English' },
    { code: 'fr', label: 'French' },
    { code: 'es', alt: 'is', label: 'Spanish' },
    { code: 'nl', label: 'Dutch' },
    { code: 'de', alt: 'of', label: 'German' }
];

const CategoriesModule = () => {
    const [categories, setCategories] = useState([]);
    const [items, setItems] = useState([]);
    const [activeTab, setActiveTab] = useState('categories'); // 'categories', 'items'

    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Filters & Search
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('All');

    // Modal States
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [confirmModalData, setConfirmModalData] = useState({ isOpen: false, id: null });

    // Form States
    const [formData, setFormData] = useState({
        id: '',
        icon: 'football',
        color: '#2B7FD9',
        order: 0,
        categoryId: '',
        translations: {},
        status: 'online'
    });

    useEffect(() => {
        const unsubCat = onSnapshot(query(collection(db, 'master_categories'), orderBy('order', 'asc')), (snap) => {
            setCategories(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });

        const unsubItem = onSnapshot(query(collection(db, 'master_category_items'), orderBy('order', 'asc')), (snap) => {
            setItems(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        return () => {
            unsubCat();
            unsubItem();
        };
    }, []);

    // Filtered Data
    const filteredData = useMemo(() => {
        let baseData = [];
        if (activeTab === 'categories') baseData = categories;
        else baseData = items;

        return baseData.filter(doc => {
            const matchesSearch = searchQuery === '' ||
                Object.values(doc.translations || {}).some(val => {
                    const str = typeof val === 'object' ? (val.name || '') : (val || '');
                    return str.toLowerCase().includes(searchQuery.toLowerCase());
                });

            const matchesFilter = activeTab === 'categories' || categoryFilter === 'All' || doc.categoryId === categoryFilter;

            return matchesSearch && matchesFilter;
        });
    }, [activeTab, categories, items, searchQuery, categoryFilter]);

    const handleOpenModal = (record = null) => {
        if (record) {
            setEditingId(record.id);
            setFormData({
                id: record.id || '',
                icon: record.icon || 'football',
                color: record.color || '#2B7FD9',
                order: record.order || 0,
                categoryId: record.categoryId || '',
                id: record.id || '',
                translations: record.translations || {},
                status: record.status || 'online'
            });
        } else {
            setEditingId(null);
            const currentCount = activeTab === 'categories' ? categories.length : items.length;
            setFormData({
                id: '',
                icon: 'football',
                color: '#2B7FD9',
                order: currentCount + 1,
                categoryId: categoryFilter !== 'All' ? categoryFilter : '',
                id: '',
                translations: {},
                status: 'online'
            });
        }
        setShowModal(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        const collectionName = activeTab === 'categories' ? 'master_categories' : 'master_category_items';

        try {
            const dataToSave = {
                ...formData,
                updatedAt: serverTimestamp()
            };

            // Ensure top-level name field exists for app compatibility
            const enTrans = formData.translations?.en;
            const enName = typeof enTrans === 'object' ? (enTrans.name || '') : (enTrans || '');
            dataToSave.name = enName;

            // Auto-generate ID if missing for new records
            if (!editingId && !dataToSave.id) {
                if (!enName) {
                    alert("Please enter English (EN) name first or provide a Unique ID.");
                    setIsSaving(false);
                    return;
                }
                const slug = enName.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '_');
                dataToSave.id = activeTab === 'categories' ? slug : `${dataToSave.categoryId || 'item'}_${slug}`;
            }

            // Clean data based on type
            if (activeTab !== 'categories') {
                delete dataToSave.icon;
                delete dataToSave.color;
            } else {
                delete dataToSave.categoryId;
            }

            if (editingId) {
                await updateDoc(doc(db, collectionName, editingId), dataToSave);
            } else {
                // Generate a descriptive ID from English name
                const englishName = activeTab === 'categories'
                    ? (formData.translations?.en?.name || 'new_category')
                    : (formData.translations?.en || 'new_item');

                const slug = englishName.toLowerCase().trim().replace(/\s+/g, '_').replace(/[^\w-]+/g, '');
                let finalId = slug;

                // For items and options, optionally prepend categoryId to ensure uniqueness
                if (activeTab !== 'categories' && formData.categoryId) {
                    finalId = `${formData.categoryId}_${slug}`;
                }

                // Add id and name fields to data for consistency
                dataToSave.id = finalId;
                if (activeTab !== 'categories') {
                    dataToSave.name = englishName;
                }

                // Use setDoc for all to ensure descriptive Document IDs
                await setDoc(doc(db, collectionName, finalId), dataToSave);
            }
            setShowModal(false);
        } catch (error) {
            console.error("Error saving record:", error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        const collectionName = activeTab === 'categories' ? 'master_categories' : 'master_category_items';
        await deleteDoc(doc(db, collectionName, id));
        setConfirmModalData({ isOpen: false, id: null });
    };

    const handleToggleStatus = async (record) => {
        const collectionName = activeTab === 'categories' ? 'master_categories' : 'master_category_items';
        const newStatus = record.status === 'offline' ? 'online' : 'offline';
        try {
            await updateDoc(doc(db, collectionName, record.id), {
                status: newStatus,
                updatedAt: serverTimestamp()
            });
        } catch (error) {
            console.error("Error toggling status:", error);
        }
    };

    return (
        <div className="content-section fade-in">
            <div className="content-header">
                <div>
                    <h2 className="content-title">Structure Management</h2>
                    <p style={{ margin: 0, color: 'var(--text-tertiary)', fontSize: '14px' }}>
                        Manage Master Categories, Items, and Options with Multilingual Support.
                    </p>
                </div>
            </div>

            {/* Navigation & Controls */}
            <div className="dashboard-controls" style={{ marginBottom: '24px' }}>
                <div className="filter-pill-container" style={{ display: 'flex', gap: '12px', background: 'var(--bg-secondary)', padding: '6px', borderRadius: '14px', width: 'fit-content' }}>
                    <button className={`pill ${activeTab === 'categories' ? 'active' : ''}`} onClick={() => { setActiveTab('categories'); setCategoryFilter('All'); }}>
                        <FaLayerGroup /> Categories
                    </button>
                    <button className={`pill ${activeTab === 'items' ? 'active' : ''}`} onClick={() => setActiveTab('items')}>
                        <FaTags /> Items
                    </button>
                </div>

                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <div className="search-bar-mini">
                        <FaSearch className="search-icon-mini" />
                        <input
                            type="text"
                            placeholder={`Search ${activeTab}...`}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>

                    {activeTab !== 'categories' && (
                        <div className="filter-dropdown-custom">
                            <FaFilter style={{ marginRight: '8px', color: 'var(--text-tertiary)' }} />
                            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                                <option value="All">All Categories</option>
                                {categories.map(cat => (
                                    <option key={cat.id} value={cat.id}>
                                        {cat.translations?.en?.name || cat.id}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <button className="btn-add" onClick={() => handleOpenModal()}>
                        <FaPlus style={{ marginRight: '8px' }} /> Add {activeTab.slice(0, -1)}
                    </button>
                </div>
            </div>

            {/* Table */}
            {loading ? (
                <div className="loading-state">Loading structural data...</div>
            ) : (
                <div className="table-container">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th style={{ width: '80px' }}>Order</th>
                                {activeTab === 'categories' ? <th>Identity</th> : <th>Category</th>}
                                <th>Translations (Names)</th>
                                <th style={{ width: '100px' }}>Status</th>
                                <th style={{ width: '120px' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.length > 0 ? (
                                filteredData.map(row => (
                                    <tr key={row.id}>
                                        <td><strong>{row.order}</strong></td>
                                        <td>
                                            {activeTab === 'categories' ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <div className="icon-badge" style={{ background: 'var(--bg-secondary)', padding: '8px', borderRadius: '10px' }}>
                                                        {renderIcon(row.icon, 22)}
                                                    </div>
                                                    <div style={{ width: '14px', height: '14px', borderRadius: '50%', background: row.color, border: '2px solid white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}></div>
                                                </div>
                                            ) : (
                                                <span className="category-tag-v4">
                                                    {categories.find(c => c.id === row.categoryId)?.translations?.en?.name || 'Unknown'}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="translation-grid-mini">
                                                {LANGUAGES.map(lang => {
                                                    const val = row.translations?.[lang.code] || row.translations?.[lang.alt];
                                                    const name = typeof val === 'object' ? val.name : val;
                                                    return (
                                                        <div key={lang.code} className="trans-chip">
                                                            <span className="lang-code">{lang.code.toUpperCase()}</span>
                                                            <span className="lang-val">{name || '---'}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <label className="switch-toggle">
                                                    <input 
                                                        type="checkbox" 
                                                        checked={row.status !== 'offline'} 
                                                        onChange={() => handleToggleStatus(row)} 
                                                    />
                                                    <span className="slider-round"></span>
                                                </label>
                                                <span style={{ 
                                                    fontSize: '12px', 
                                                    fontWeight: '600', 
                                                    color: row.status === 'offline' ? 'var(--text-tertiary)' : 'var(--success)',
                                                    minWidth: '50px'
                                                }}>
                                                    {row.status === 'offline' ? 'Offline' : 'Online'}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="action-buttons">
                                                <button className="btn-action edit" onClick={() => handleOpenModal(row)}><FaEdit /></button>
                                                <button className="btn-action delete" onClick={() => setConfirmModalData({ isOpen: true, id: row.id })}><FaTrash /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                (<tr>
                                    <td colSpan="5" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>
                                        No structural data found matching your criteria.
                                    </td>
                                </tr>)
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Universal Modal */}
            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-content-custom" style={{ maxWidth: activeTab === 'categories' ? '800px' : '650px' }}>
                        <div className="modal-header-custom">
                            <h2>{editingId ? 'Edit' : 'Add'} {activeTab.charAt(0).toUpperCase() + activeTab.slice(1, -1)}</h2>
                            <button className="close-btn" onClick={() => setShowModal(false)}>&times;</button>
                        </div>

                        <form onSubmit={handleSave} className="edit-form">
                            <div className="edit-form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>

                                {activeTab === 'categories' ? (
                                    <>
                                        <div className="form-group-custom">
                                            <label>Icon Name</label>
                                            <input type="text" value={formData.icon} onChange={(e) => setFormData({ ...formData, icon: e.target.value })} required />
                                        </div>
                                        <div className="form-group-custom">
                                            <label>Theme Color</label>
                                            <input type="text" value={formData.color} onChange={(e) => setFormData({ ...formData, color: e.target.value })} required />
                                        </div>
                                    </>
                                ) : (
                                    <div className="form-group-custom">
                                        <label>Master Category</label>
                                        <select value={formData.categoryId} onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })} required>
                                            <option value="">Select Category</option>
                                            {categories.map(c => <option key={c.id} value={c.id}>{c.translations?.en?.name || c.id}</option>)}
                                        </select>
                                    </div>
                                )}
                                <div className="form-group-custom">
                                    <label>Display Order</label>
                                    <input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })} required />
                                </div>
                                {formData.id && (
                                    <div className="form-group-custom">
                                        <label>Internal ID (Read-only)</label>
                                        <input type="text" value={formData.id} disabled style={{ background: 'var(--bg-secondary)' }} />
                                    </div>
                                )}
                                <div className="form-group-custom">
                                    <label>Current Status</label>
                                    <select 
                                        value={formData.status} 
                                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                        style={{ border: '1px solid var(--border-light)', borderRadius: '12px', padding: '10px' }}
                                    >
                                        <option value="online">Online (Visible)</option>
                                        <option value="offline">Offline (Hidden)</option>
                                    </select>
                                </div>
                            </div>

                            <hr style={{ margin: '24px 0', border: '0', borderTop: '1px solid var(--border-light)' }} />

                            <h3 style={{ fontSize: '15px', color: 'var(--primary-blue)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <FaGlobe /> Language Translations
                            </h3>

                            <div className="translations-editor-scroll" style={{ maxHeight: '400px', overflowY: 'auto', padding: '4px' }}>
                                {LANGUAGES.map(lang => (
                                    <div key={lang.code} className="lang-group-box">
                                        <div className="lang-header">
                                            <span>{lang.label}</span>
                                            <span className="lang-tag">{lang.code.toUpperCase()}</span>
                                        </div>
                                        {activeTab === 'categories' ? (
                                            <div className="edit-form-grid" style={{ padding: '0' }}>
                                                <div className="form-group-custom">
                                                    <label>Name</label>
                                                    <input
                                                        type="text"
                                                        value={formData.translations?.[lang.code]?.name || ''}
                                                        onChange={(e) => {
                                                            const t = { ...formData.translations };
                                                            t[lang.code] = { ...(t[lang.code] || {}), name: e.target.value };
                                                            setFormData({ ...formData, translations: t });
                                                        }}
                                                        required={lang.code === 'en'}
                                                    />
                                                </div>
                                                <div className="form-group-custom">
                                                    <label>Description</label>
                                                    <input
                                                        type="text"
                                                        value={formData.translations?.[lang.code]?.description || ''}
                                                        onChange={(e) => {
                                                            const t = { ...formData.translations };
                                                            t[lang.code] = { ...(t[lang.code] || {}), description: e.target.value };
                                                            setFormData({ ...formData, translations: t });
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="form-group-custom" style={{ marginTop: '8px' }}>
                                                <label>Display Name</label>
                                                <input
                                                    type="text"
                                                    value={formData.translations?.[lang.code] || formData.translations?.[lang.alt] || ''}
                                                    onChange={(e) => {
                                                        const t = { ...formData.translations };
                                                        t[lang.code] = e.target.value;
                                                        // Also sync alt code if exists for compatibility
                                                        if (lang.alt) t[lang.alt] = e.target.value;
                                                        setFormData({ ...formData, translations: t });
                                                    }}
                                                    required={lang.code === 'en'}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className="modal-actions-custom" style={{ marginTop: '24px' }}>
                                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn-save" disabled={isSaving}>
                                    {isSaving ? 'Saving...' : (editingId ? 'Update Structural Data' : 'Add Structural Data')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
                .dashboard-controls { display: flex; justify-content: space-between; align-items: center; gap: 20px; flex-wrap: wrap; }
                .filter-pill-container .pill { display: flex; alignItems: center; gap: 8px; border: none; padding: 10px 18px; border-radius: 10px; cursor: pointer; transition: all 0.3s; color: var(--text-secondary); background: transparent; font-weight: 600; font-size: 14px; }
                .filter-pill-container .pill.active { background: white; border-radius: 9px; color: var(--primary-blue); box-shadow: 0 4px 10px rgba(0,0,0,0.05); }
                .filter-dropdown-custom { display: flex; align-items: center; background: var(--bg-card); border: 1px solid var(--border-light); padding: 0 12px; border-radius: 12px; }
                .filter-dropdown-custom select { border: none; background: transparent; padding: 10px 0; color: var(--text-primary); font-size: 14px; outline: none; min-width: 150px; }
                .translation-grid-mini { display: flex; flex-wrap: wrap; gap: 6px; }
                .trans-chip { display: flex; align-items: center; gap: 6px; background: var(--bg-secondary); border: 1px solid var(--border-light); padding: 4px 10px; border-radius: 8px; font-size: 11px; }
                .lang-code { font-weight: 800; color: var(--primary-blue); border-right: 1px solid var(--border-light); padding-right: 6px; }
                .lang-val { color: var(--text-primary); }
                .lang-group-box { margin-bottom: 12px; padding: 16px; background: var(--bg-card); border-radius: 12px; border: 1px solid var(--border-light); }
                .lang-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; border-bottom: 1px dashed var(--border-light); padding-bottom: 8px; font-weight: 700; color: var(--text-secondary); font-size: 13px; }
                .lang-tag { background: var(--primary-blue); color: white; padding: 2px 6px; border-radius: 4px; font-size: 10px; }
                .loading-state { text-align: center; padding: 80px; font-size: 18px; color: var(--text-tertiary); background: var(--bg-card); border-radius: 20px; border: 2px dashed var(--border-color); }
                
                .status-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
                .status-dot.online { background: #10b981; box-shadow: 0 0 8px rgba(16, 185, 129, 0.4); }
                .status-dot.offline { background: #ef4444; }
                
                .status-pill { transition: all 0.2s ease; }
                .status-pill:hover { filter: brightness(0.95); transform: translateY(-1px); }
                .status-pill:active { transform: translateY(0); }

                /* Toggle Switch Styles */
                .switch-toggle { position: relative; display: inline-block; width: 42px; height: 22px; }
                .switch-toggle input { opacity: 0; width: 0; height: 0; }
                .slider-round { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #ccc; transition: .4s; border-radius: 34px; }
                .slider-round:before { position: absolute; content: ""; height: 16px; width: 16px; left: 3px; bottom: 3px; background-color: white; transition: .4s; border-radius: 50%; box-shadow: 0 2px 4px rgba(0,0,0,0.2); }
                input:checked + .slider-round { background-color: #10b981; }
                input:focus + .slider-round { box-shadow: 0 0 1px #10b981; }
                input:checked + .slider-round:before { transform: translateX(20px); }
            `}</style>

            <ConfirmModal
                isOpen={confirmModalData.isOpen}
                onClose={() => setConfirmModalData({ isOpen: false, id: null })}
                onConfirm={() => handleDelete(confirmModalData.id)}
                title={`Delete ${activeTab.slice(0, -1)}?`}
                message={`Are you sure you want to delete this ${activeTab.slice(0, -1)}? This action will permanently remove it and cannot be undone.`}
                confirmText="Yes, Delete"
                type="danger"
            />
        </div>
    );
};

export default CategoriesModule;
